
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useScrollLock } from '../../hooks/useScrollLock';
import { AlertCircleIcon, CheckCircleIcon, HelpCircleIcon, TrashIcon, XIcon } from './Icons';


interface ConfirmationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    onThirdAction?: () => void;
    title: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    thirdActionLabel?: string;
    thirdActionStyle?: 'primary' | 'secondary' | 'danger';
    isDestructive?: boolean;
    isLoading?: boolean;
    showCancel?: boolean;
    confirmStyle?: 'primary' | 'danger' | 'success' | 'pending' | 'secondary';
    hideIcon?: boolean;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    onThirdAction,
    title,
    message,
    confirmLabel = "Confirm",
    cancelLabel = "Cancel",
    thirdActionLabel,
    thirdActionStyle = 'primary',
    isDestructive = false,
    isLoading = false,
    showCancel = true,
    confirmStyle,
    hideIcon = false
}) => {
    useScrollLock(isOpen);
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setIsMounted(true);
        } else {
            const timer = setTimeout(() => setIsMounted(false), 300);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    if (!isMounted && !isOpen) return null;

    const Icon = isDestructive ? TrashIcon : (showCancel ? HelpCircleIcon : AlertCircleIcon);
    const iconColor = isDestructive ? 'text-red-500 bg-red-100 dark:bg-red-900/30' : (showCancel ? 'text-primary bg-primary/10' : 'text-amber-500 bg-amber-100 dark:bg-amber-900/30');

    const getThirdActionClass = () => {
        switch (thirdActionStyle) {
            case 'danger': return 'bg-red-500 hover:bg-red-600 shadow-red-500/20 text-white';
            case 'secondary': return 'bg-gray-500 hover:bg-gray-600 shadow-gray-500/20 text-white';
            case 'primary': default: return 'bg-primary hover:bg-primary-hover shadow-primary/20 text-white';
        }
    };

    const getConfirmActionClass = () => {
        if (confirmStyle === 'pending') return 'bg-blue-600 hover:bg-blue-700 shadow-blue-600/20 text-white';
        if (confirmStyle === 'success') return 'bg-green-600 hover:bg-green-700 shadow-green-600/20 text-white';
        if (confirmStyle === 'danger' || isDestructive) return 'bg-red-500 hover:bg-red-600 shadow-red-500/20 text-white';
        if (confirmStyle === 'secondary') return 'bg-gray-500 hover:bg-gray-600 shadow-gray-500/20 text-white';
        return 'bg-primary hover:bg-primary-hover shadow-primary/20 text-white';
    };

    return ReactDOM.createPortal(
        <div className={`fixed inset-0 z-[100] flex items-center justify-center p-4 transition-all duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />


            {/* Modal Content */}
            <div className={`relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full mx-auto overflow-hidden transition-all duration-300 transform ${isOpen ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'}`}>
                
                {/* Close Button (top right) */}
                <button 
                    onClick={onClose}
                    className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 transition-colors"
                >
                    <XIcon className="h-5 w-5" />
                </button>

                <div className="p-6">
                    <div className="flex flex-col items-center text-center">
                        {!hideIcon && (
                            <div className={`p-3 rounded-lg mb-4 ${iconColor}`}>
                                <Icon className="h-8 w-8" />
                            </div>
                        )}
                        
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                            {title}
                        </h3>

                        <p className="text-base text-gray-600 dark:text-gray-400 mb-6 leading-relaxed whitespace-pre-line">
                            {message}
                        </p>

                        <div className="flex flex-col-reverse sm:flex-row gap-3 w-full">
                            {showCancel && (
                                <button
                                    onClick={onClose}
                                    disabled={isLoading}
                                    className="flex-1 px-4 py-2.5 rounded-lg font-semibold text-sm text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-all active:scale-95 disabled:opacity-50"
                                >
                                    {cancelLabel}
                                </button>
                            )}
                            
                            {thirdActionLabel && onThirdAction && (
                                <button
                                    onClick={onThirdAction}
                                    disabled={isLoading}
                                    className={`flex-1 px-4 py-2.5 rounded-lg font-semibold text-sm shadow-md transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2 ${getThirdActionClass()}`}
                                >
                                    {thirdActionLabel}
                                </button>
                            )}
                            
                            <button
                                onClick={onConfirm}
                                disabled={isLoading}
                                className={`flex-1 px-4 py-2.5 rounded-lg font-semibold text-sm shadow-md transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2 ${getConfirmActionClass()}`}
                            >
                                {isLoading && (
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                )}
                                {confirmLabel}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ConfirmationModal;
